import { AfterViewInit, Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import {
  AgCartesianAxisOptions,
  AgChart,
  AgChartOptions,
  AgColumnSeriesOptions,
  AgLineSeriesOptions,
} from 'ag-charts-community';
import { AgChartInstance, AgSeriesNodeClickParams } from 'ag-charts-community/dist/esm/es6/chart/agChartOptions';
import { Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { BaseComponent } from '../../base/base.component';
import { Colours } from '../../../constants';
import { BarChartAxisType } from '../../../models';
import { FormatService } from '../../../services';

@Component({
  selector: 'app-bar-chart',
  templateUrl: './bar-chart.component.html',
  styleUrl: './bar-chart.component.scss',
})
export class BarChartComponent<T> extends BaseComponent implements AfterViewInit {
  @ViewChild('chart') chart: ElementRef<HTMLDivElement>;

  @Input() header: string = '';
  @Input() subHeader: string = '';
  @Input() xKey: string;
  @Input() xAxisName: string;
  @Input() xFormat: BarChartAxisType = BarChartAxisType.text;
  @Input() xRotation: number = 0;

  @Input() yKey: string;
  @Input() yName: string;
  @Input() yAxisName: string;
  @Input() yFormat: BarChartAxisType = BarChartAxisType.text;
  @Input() yRotation: number = 0;

  @Input() yKeyRight: string;
  @Input() yNameRight: string;
  @Input() yAxisNameRight: string;
  @Input() yFormatRight: BarChartAxisType = BarChartAxisType.text;
  @Input() yRotationRight: number = 0;

  @Input() showLegend = false;
  @Input() data$: Observable<T[]>;
  @Input() subHeaderFunc: (data: T[]) => string;
  @Input() dateFormatFunc: (date: Date) => string;
  @Output() clicked = new EventEmitter<T>();

  private agChart: AgChartInstance;
  private options: AgChartOptions;

  constructor(private formatService: FormatService) {
    super();
  }

  ngAfterViewInit(): void {
    this.drawChart();
  }

  formatSummary(count: number, total: number): string {
    return (
      `${$localize`Total`}: ${this.formatValue(total, this.yFormat, false)}, ` +
      `${$localize`Count`}: ${this.formatValue(count, this.yFormatRight, true)}`
    );
  }

  private drawChart() {
    this.data$.pipe(takeUntil(this.destroy$)).subscribe((data: T[]) => {
      this.getOptions(data);
      if (!this.agChart) {
        this.agChart = AgChart.create(this.options);
      } else {
        AgChart.update(this.agChart, this.options);
      }
    });
  }

  private getOptions(data: T[]) {
    this.options = {
      autoSize: true,
      container: this.chart?.nativeElement,
      title: {
        text: this.header,
      },
      subtitle: {
        text: this.subHeaderFunc ? this.subHeaderFunc(data) : this.subHeader,
      },
      data: data,
      series: this.getSeries(),
      axes: this.getAxes(data),
      legend: {
        enabled: this.showLegend,
        position: 'bottom',
      },
    };
  }

  private getSeries(): (AgColumnSeriesOptions | AgLineSeriesOptions)[] {
    const series: (AgColumnSeriesOptions | AgLineSeriesOptions)[] = [
      {
        type: 'column',
        xKey: this.xKey,
        yKey: this.yKey,
        yName: this.yName ?? this.yKey,
        fill: Colours.blue,
        ...this.seriesDefaults(),
      },
    ];

    if (this.yKeyRight) {
      series.push({
        type: 'line',
        xKey: this.xKey,
        yKey: this.yKeyRight,
        yName: this.yNameRight ?? this.yKeyRight,
        ...this.seriesDefaults(),
      });
    }

    return series;
  }

  private seriesDefaults(): AgLineSeriesOptions {
    return {
      highlightStyle: {
        item: {
          fill: Colours.green,
        },
      },
      tooltip: {
        renderer: (params) => ({
          title: `${this.xAxisName ? `${this.xAxisName}: ` : ''}${this.formatValue(params.xValue, this.xFormat)}`,
          content: this.formatSummary(params.datum[this.yKeyRight], params.datum[this.yKey]),
        }),
      },
      listeners: {
        nodeClick: (event: AgSeriesNodeClickParams<any>) => this.clicked.emit(event.datum),
      },
    };
  }

  private getAxes(data: T[]): AgCartesianAxisOptions[] {
    const axes: AgCartesianAxisOptions[] = [
      {
        title: {
          text: this.xAxisName,
        },
        position: 'bottom',
        ...this.axisDefaults(this.xFormat, this.xRotation),
      },
      {
        title: {
          text: this.yAxisName,
        },
        position: 'left',
        keys: [this.yKey],
        ...this.axisDefaults(this.yFormat, this.yRotation),
      },
    ];

    if (this.yKeyRight) {
      axes.push({
        title: {
          text: this.yAxisNameRight,
        },
        position: 'right',
        keys: [this.yKeyRight],
        ...this.axisDefaults(this.yFormatRight, this.yRotationRight),
      });
    }

    return axes;
  }

  private axisDefaults(type: BarChartAxisType, rotation: number): any {
    return {
      type: type === BarChartAxisType.number || type === BarChartAxisType.currency ? 'number' : 'category',
      tick: {
        minSpacing: 5,
      },
      label: {
        rotation: rotation,
        formatter: (params) => this.formatValue(params.value, type),
      },
    };
  }

  private formatValue(value: any, type: BarChartAxisType, rounded = true): any {
    switch (type) {
      case BarChartAxisType.number:
        return rounded ? this.formatService.numberShort(value) : this.formatService.number(value);
      case BarChartAxisType.currency:
        return rounded ? this.formatService.currencyShort(value, '£') : this.formatService.currency(value, '£');
      case BarChartAxisType.date:
        return this.dateFormatFunc
          ? this.dateFormatFunc(value)
          : `${this.formatService.dayOfWeekShort(value)} ${this.formatService.date(value)}`;
      default:
        return value;
    }
  }
}
